import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../../services/api';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { useExpenditureLabels, useDecimalFormat } from '../../../contexts/ExpenditureLabelsContext';
import TypeAheadLookup from '../../../components/TypeAheadLookup';
import { LoadingSpinner, EmptyState } from '../../../components/layout/SharedComponents';
import {
  ChevronDown, ChevronLeft, ChevronRight, X, Plus, Trash2,
  Layers, FileText, CheckSquare, Split, Paperclip,
  ScrollText, StickyNote, Package, Activity, Save,
  TrendingUp, BarChart2, Upload, Search, Wand2, Download,
  RefreshCw, Edit, FileSpreadsheet, ListOrdered, AlignJustify, Users
} from 'lucide-react';
import { SortableTableHeader, useSortState, sortData } from '../../../components/SortableTableHeader';

export default function AddCatalogItemModal({ open, onClose, onAdd }) {
  const [search, setSearch] = useState('');
  const [items, setItems] = useState([]);
  const [catSort, handleCatSort] = useSortState('item_code', 'asc');
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);

  // Load all items on open; then filter live as user types
  const doSearch = useCallback(async (query) => {
    setLoading(true);
    try {
      const res = await api.get('/api/data/items', { params: { search: query, page_size: 50 } });
      setItems(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch {}
    setLoading(false);
  }, []);

  // Initial load + debounced live search
  useEffect(() => {
    if (!open) { setSearch(''); setItems([]); setTotal(0); return; }
    doSearch(''); // show everything on open
  }, [open, doSearch]);

  // Debounce as user types
  useEffect(() => {
    const timer = setTimeout(() => { if (open) doSearch(search); }, 250);
    return () => clearTimeout(timer);
  }, [search, open, doSearch]);

  const highlightMatch = (text, query) => {
    if (!query || !text) return text;
    const idx = text.toLowerCase().indexOf(query.toLowerCase());
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <mark className="bg-primary/20 text-primary font-semibold rounded-sm px-0.5">
          {text.slice(idx, idx + query.length)}
        </mark>
        {text.slice(idx + query.length)}
      </>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Search className="h-5 w-5 text-primary" /> Add Catalog Item
          </DialogTitle>
        </DialogHeader>

        {/* Live search input */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Start typing — e.g. coffee, laptop, chair..."
            className="h-10 w-full rounded-lg border border-input bg-background pl-9 pr-4 text-sm focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
            autoFocus
            aria-label="Search catalog items"
            data-testid="catalog-search-input"
          />
          {loading && (
            <RefreshCw className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground animate-spin" />
          )}
          {search && !loading && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Results count */}
        <p className="text-xs text-muted-foreground mb-2">
          {loading ? 'Searching...' : search
            ? `${items.length} result${items.length !== 1 ? 's' : ''} for "${search}"${total > items.length ? ` (showing first ${items.length} of ${total})` : ''}`
            : `${total} items in catalog`}
        </p>

        {/* Scrollable results */}
        <div className="flex-1 overflow-y-auto rounded-xl border">
          {!loading && items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <Search className="h-8 w-8 text-muted-foreground/30 mb-3" />
              <p className="text-sm font-medium text-muted-foreground">
                {search ? `No items found for "${search}"` : 'No items in catalog'}
              </p>
              <p className="text-xs text-muted-foreground mt-1">Try a different keyword</p>
            </div>
          ) : (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-muted/80 backdrop-blur">
                <tr>
                  <SortableTableHeader label="Item Code" field="item_code" sortState={catSort} onSort={handleCatSort} />
                  <SortableTableHeader label="Description" field="description" sortState={catSort} onSort={handleCatSort} />
                  <SortableTableHeader label="Category" field="category_code" sortState={catSort} onSort={handleCatSort} />
                  <SortableTableHeader label="Type" field="item_type" sortState={catSort} onSort={handleCatSort} />
                  <th className="w-16"></th>
                </tr>
              </thead>
              <tbody>
                {sortData(items, catSort.field, catSort.dir).map(it => (
                  <tr key={it.item_code}
                    className="border-b hover:bg-primary/5 cursor-pointer transition-colors"
                    onClick={() => { onAdd(it); onClose(); }}
                  >
                    <td className="px-3 py-2 font-mono text-primary font-medium">
                      {highlightMatch(it.item_code, search)}
                    </td>
                    <td className="px-3 py-2 max-w-[240px]">
                      {highlightMatch(it.outline, search)}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">{it.category_code}</td>
                    <td className="px-3 py-2">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                        it.item_type === 'Fixed Asset' ? 'bg-blue-100 text-blue-700' :
                        it.item_type === 'Current Asset' ? 'bg-purple-100 text-purple-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>{it.item_type}</span>
                    </td>
                    <td className="px-3 py-2" onClick={e => e.stopPropagation()}>
                      <Button size="sm" className="h-6 text-xs px-2"
                        onClick={() => { onAdd(it); onClose(); }}>
                        Add
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
